'use client';

/**
 * Forgot Password Form Component
 * Magento 2 production-style — initiates password reset
 */

import { useState } from 'react';
import Link from 'next/link';

export function ForgotPasswordForm() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset link');
      }

      // Deliberately the same confirmation whether or not the address has an
      // account — the API tells us nothing more, by design.
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reset link');
    } finally {
      setIsLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="register-form-wrapper">
        <div className="account-alert account-alert--success mb-4">
          If an account exists for that email address, we&apos;ve sent a link to
          reset your password. The link can be used once and expires in 60
          minutes.
        </div>
        <div className="register-links">
          <Link href="/login" className="register-signin-link">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="register-form-wrapper">
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="account-alert account-alert--warning mb-4">
            {error}
          </div>
        )}

        <fieldset className="register-fieldset">
          <legend className="register-fieldset-legend">
            <span>Password Reset</span>
          </legend>

          <p className="register-fieldset-note">
            Enter your email address and we&apos;ll send you a link to reset your password.
          </p>

          <div className="register-form-group">
            <label htmlFor="forgot-email">
              Email <span className="required">*</span>
            </label>
            <input
              id="forgot-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <p className="register-required-note">* Required Fields</p>
        </fieldset>

        <div className="register-actions">
          <button
            type="submit"
            className="register-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Sending link...' : 'Send Reset Link'}
          </button>
        </div>

        <div className="register-links">
          <Link href="/login" className="register-signin-link">
            Remember your password? Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
